import { Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { ContentSection } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';

import { ContentStatusBadge } from './content-badges';

/** Column model of the sections table; `meta` ties columns to the list's sorts and filters. */
export function useSectionColumns(storeId: string) {
  const { t } = useTranslation('content');
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<ContentSection>>>(
    () => [
      {
        id: 'name',
        header: t('columns.section'),
        cell: ({ row }) => (
          <div className="min-w-0">
            <Link
              to="/s/$storeId/content/sections/$sectionId"
              params={{ storeId, sectionId: row.original.id }}
              className="block truncate font-medium hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {row.original.name}
            </Link>
            <code className="text-muted-foreground block truncate text-xs">
              {row.original.handle}
            </code>
          </div>
        ),
        meta: { label: t('columns.section'), sortField: 'name', lockVisibility: true },
      },
      {
        id: 'blocks',
        header: t('columns.blocks'),
        cell: ({ row }) => (
          <span className="tabular-nums">
            {t('columns.blockCount', { count: row.original.blocks.length })}
          </span>
        ),
        meta: { label: t('columns.blocks') },
      },
      {
        id: 'status',
        header: t('columns.status'),
        cell: ({ row }) => <ContentStatusBadge status={row.original.status} />,
        meta: { label: t('columns.status'), filterKey: 'status' },
      },
      {
        id: 'updatedAt',
        header: t('columns.updated'),
        cell: ({ row }) => (
          <span className="text-muted-foreground" title={format.utc(row.original.updatedAt)}>
            {format.relative(row.original.updatedAt)}
          </span>
        ),
        meta: { label: t('columns.updated'), sortField: 'updatedAt', filterKey: 'updatedAt' },
      },
    ],
    [format, storeId, t],
  );
}
